/**
 * JCTC Common Scripts
 */

import { initBlogAjax } from './blog-ajax.js';
import { initEventFeedAjax } from './event-feed-ajax.js';
import { initEventsAjax, initEventsPreviewScale } from './events-ajax.js';
import initCf7Turnstile from './modules/cf7-turnstile.js';
import initEventCardTitleFit from './modules/event-card-title-fit.js';
import initEvents from './modules/events.js';
import initLqipReveal from './modules/lqip-reveal.js';
import initSwiperViewportAutoplay from './modules/swiper-viewport-autoplay.js';
import { initPodcastsAjax } from './podcasts-ajax.js';

// JCTCTAS-152: initHeroVideoViewport/initBookingFormModal/initEventCalendars/
// initStaffModal/initRecommendedEventsReorder/initRecommendedGlowCanvas/
// initRecommendedGlowViewport used to be imported and called here
// unconditionally -- each now lives in its own standalone Vite entry
// (assets/src/js/entries/*.js), enqueued only on a page that actually has
// the matching block/ACF layout (see inc/scripts.php's
// jctc_block_conditional_assets()/jctc_layout_conditional_scripts()/
// jctc_recommended_glow_script()/jctc_recommended_events_reorder_script()).

(($) => {
    // 1. Mobile & Side Menu Logic
    const initMenu = () => {
        const triggers = document.querySelectorAll('.js-menu-toggle');
        const overlay = document.querySelector('.js-menu-overlay');
        const overlayInner = document.querySelector('.menu-overlay__inner');
        if (!triggers.length || !overlay) return;

        // JCTCTAS-248: which trigger opened the overlay, so Escape/close can
        // return focus there -- both the header's own hamburger button and
        // the overlay's own close (X) button share the same .js-menu-toggle
        // class (see the click handler below, which toggles based on
        // whichever one was actually clicked), so this can't just be "the
        // first trigger" or "the last clicked trigger": it must only be set
        // when a click is genuinely OPENING the overlay, not when the close
        // button inside it is clicked to close it again.
        let lastOpenTrigger = null;

        const toggleMenu = (state) => {
            if (state === 'close') {
                overlay.classList.remove('is-open');
                document.body.classList.remove('_lock');
                triggers.forEach((t) => {
                    t.classList.remove('is-active');
                });
            } else {
                overlay.classList.toggle('is-open');
                document.body.classList.toggle('_lock');
                triggers.forEach((t) => {
                    t.classList.toggle('is-active');
                });
            }

            // Read the overlay's own resulting state back, rather than
            // tracking a separate boolean, so aria-expanded can never drift
            // out of sync with what .is-open actually ended up as.
            const isOpen = overlay.classList.contains('is-open');
            triggers.forEach((t) => {
                t.setAttribute('aria-expanded', String(isOpen));
            });
        };

        triggers.forEach((trigger) => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!overlay.classList.contains('is-open')) {
                    lastOpenTrigger = trigger;
                }
                toggleMenu();
            });
        });

        overlay.addEventListener('click', (e) => {
            if (overlayInner && !overlayInner.contains(e.target)) toggleMenu('close');
        });

        // JCTCTAS-248: this overlay previously had NO Escape handling and NO
        // Tab focus-trap at all (confirmed live: Tab walked straight through
        // to the page/WPConsent behind it, and Escape did nothing) -- unlike
        // the two modals (JCTCTAS-232/236), which at least attempted a trap
        // via the shared modal-behavior.js module. Mirrors that module's own
        // proven fix: capture phase + stopPropagation() on every Tab while
        // open, so a third-party global keydown handler (e.g. WPConsent, see
        // JCTCTAS-232) can't hijack focus out of the overlay first.
        document.addEventListener(
            'keydown',
            (event) => {
                if (!overlay.classList.contains('is-open')) {
                    return;
                }
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    toggleMenu('close');
                    lastOpenTrigger?.focus();
                    return;
                }
                if (event.key !== 'Tab') {
                    return;
                }
                event.stopPropagation();
                const focusable = overlayInner
                    ? overlayInner.querySelectorAll(
                          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
                      )
                    : [];
                if (!focusable.length) {
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            },
            true
        );
    };

    // 1b. Accordion for nested sub-menus inside the overlay nav (JCTCTAS
    // nav-menu accordion, 2026-09-07). inc/menus.php's JCTC_Overlay_Walker
    // only overrides start_el() -- it inherits Walker_Nav_Menu's own default
    // start_lvl()/end_lvl(), so a parent item with real children already
    // renders a normal <ul class="sub-menu"> with WordPress's own
    // "menu-item-has-children" class on its <li> (confirmed live via `wp
    // eval` against the real overlay menu). The only missing piece was
    // interactive collapse/expand -- Figma's static mockup only shows every
    // item pre-expanded (no collapsed-state layer exists in the file to
    // read), so "closed by default" and the chevron icon/rotation below are
    // both a product decision made here, not a value read from Figma.
    const initMenuAccordion = () => {
        const toggles = document.querySelectorAll(
            '.menu-overlay__menu > .menu-item-has-children > a'
        );
        if (!toggles.length) return;

        toggles.forEach((link, index) => {
            const item = link.parentElement;
            const submenu = item.querySelector(':scope > .sub-menu');
            if (!submenu) return;

            const submenuId = `menu-overlay-submenu-${index}`;
            submenu.id = submenuId;
            link.setAttribute('aria-expanded', 'false');
            link.setAttribute('aria-controls', submenuId);
            // Plain CSS triangle (clip-path, no SVG/mask) rather than reusing
            // .jctc-icon--arrow-main -- that icon is a tall curved "spike"
            // shape meant for directional pagination, not a caret; rotated
            // sideways for this accordion it read as an ugly, oddly-shaped
            // sliver (direct user feedback, 2026-09-07).
            link.insertAdjacentHTML(
                'beforeend',
                '<span class="menu-overlay__toggle-icon" aria-hidden="true"></span>'
            );

            link.addEventListener('click', (e) => {
                e.preventDefault();
                const isOpen = item.classList.toggle('is-open');
                link.setAttribute('aria-expanded', String(isOpen));
            });
        });
    };

    // 2. Sticky Header Logic -- IntersectionObserver against a 1px sentinel
    // right at the top of .header (see header.php) instead of a scroll
    // listener: no per-frame scroll work, only fires when the sentinel
    // actually crosses the viewport edge (top of page vs. scrolled away).
    const initStickyHeader = () => {
        const header = document.querySelector('.header');
        const sentinel = document.querySelector('.header__sentinel');
        if (!header || !sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                header.classList.toggle('is-sticky', !entry.isIntersecting);
            },
            { threshold: 0 }
        );
        observer.observe(sentinel);
    };

    // 3. Hero video fallback
    const initHeroVideo = () => {
        const video = document.querySelector('.hero__video');
        if (!video) return;

        // A YouTube/Vimeo embed (JCTCTAS-5) is an <iframe>, not an
        // HTMLMediaElement -- it has no .pause()/.playbackRate/'loadeddata'.
        // There's also no reliable way to stop a cross-origin embed's
        // baked-in autoplay from here without loading each provider's own
        // postMessage JS API just for this. Reduced-motion is instead
        // honored the same way disabling video entirely already is: remove
        // the iframe so the still poster underneath (never removed, see
        // render.php) is what's left showing.
        if (video.tagName === 'IFRAME') {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                video.remove();
                return;
            }
            const showEmbed = () => video.classList.add('is-loaded');
            video.addEventListener('load', showEmbed, { once: true });
            return;
        }

        // JCTCTAS-115: playback is now always JS-driven (no native `autoplay`
        // attribute -- see render.php's own comment) specifically so a
        // reduced-motion visitor can simply never have `.play()` called at
        // all, instead of the old approach of letting native autoplay start
        // it and reactively pausing a frame or two later.
        // modules/hero-video-viewport.js separately refuses to resume/start
        // it on scroll for the same visitor.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const speed = parseFloat(video.dataset.playbackSpeed);
        if (!Number.isNaN(speed) && speed > 0) video.playbackRate = speed;

        const showVideo = () => video.classList.add('is-loaded');
        // Playing is requested via .play() (never assumed to already be
        // running) so `showVideo()` -- which fades the video in over the
        // poster -- only ever fires once playback has actually, genuinely
        // started; .catch() still reveals on a rejected play() (e.g. an
        // autoplay-policy edge case) so the video is never left invisible
        // forever behind its own poster.
        const startPlayback = () => video.play().then(showVideo).catch(showVideo);

        // JCTCTAS-115: "wait for buffer" (default, ACF field `video_wait_for_buffer`,
        // see render.php) gates the actual playback START on `canplaythrough`
        // -- the browser's own estimate that it can play through without
        // stopping to buffer again -- instead of starting immediately and
        // risking visible stutter on a slow connection. The poster
        // (blurred via the existing LQIP mechanism) stays on screen the
        // whole time this is waiting. When the field is off, behavior
        // matches the theme's previous immediate-autoplay default.
        if (video.readyState >= 2) {
            startPlayback();
        } else {
            video.addEventListener('canplay', startPlayback, { once: true });
            video.addEventListener('loadeddata', startPlayback, { once: true });
        }
    };

    // 3b. Hero video stall recovery (JCTCTAS-168)
    //
    // Confirmed live (CDP-throttled Playwright run, see that ticket): on a
    // slow connection, this plain progressive <video> (no HLS/DASH) can
    // reach its own reported `duration` while only a fraction of the file
    // has actually downloaded, fire a `waiting` event, and then get stuck
    // there PERMANENTLY -- readyState drops and never recovers, `ended`
    // never fires, nothing else happens. Not a bug in this project's own
    // JS (verified: playbackRate stays 1, nothing here seeks or reloads the
    // element) -- native progressive video has no browser-guaranteed
    // automatic recovery from this specific stall shape. This is the fix:
    // if `waiting` doesn't resolve into `playing`/`canplay` again within
    // STALL_TIMEOUT_MS, force the browser to re-request the stalled byte
    // range via `.load()` (preserving the current position), which a plain
    // stall alone will never do on its own.
    const HERO_VIDEO_STALL_TIMEOUT_MS = 5000;

    const initHeroVideoStallRecovery = () => {
        const video = document.querySelector('.hero__video:not(.hero__video--embed)');
        const wrapper = document.querySelector('.hero__video-wrapper');
        if (!video || !wrapper) return;

        let stallTimer = null;
        let hasStartedPlaying = false;

        const clearStallTimer = () => {
            if (stallTimer) {
                clearTimeout(stallTimer);
                stallTimer = null;
            }
        };

        const hideBuffering = () => {
            wrapper.classList.remove('is-buffering');
            clearStallTimer();
        };

        // Track when playback has genuinely started
        video.addEventListener('playing', () => {
            hasStartedPlaying = true;
            hideBuffering();
        });

        video.addEventListener('timeupdate', () => {
            if (video.currentTime > 0) {
                hasStartedPlaying = true;
            }
        });

        // The recovery reseek below must not fight modules/hero-video-
        // viewport.js's own deliberate video.pause() for a scrolled-out-of-
        // view or reduced-motion visitor -- if either is true by the time
        // the reload actually finishes, only surface the buffered frame,
        // don't resume autoplay against that other module's own intent.
        const shouldResumePlayback = () => {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return false;
            }
            const rect = wrapper.getBoundingClientRect();
            return rect.bottom > 0 && rect.top < window.innerHeight;
        };

        video.addEventListener('waiting', () => {
            // JCTCTAS-549: Suppress spinner before playback has actually started
            if (!hasStartedPlaying || video.currentTime === 0) {
                return;
            }

            wrapper.classList.add('is-buffering');
            clearStallTimer();
            stallTimer = setTimeout(() => {
                const resumeAt = video.currentTime;
                video.addEventListener(
                    'loadedmetadata',
                    () => {
                        video.currentTime = resumeAt;
                        if (shouldResumePlayback()) {
                            video.play().catch(() => {});
                        }
                    },
                    { once: true }
                );
                video.load();
            }, HERO_VIDEO_STALL_TIMEOUT_MS);
        });

        ['playing', 'canplay', 'ended'].forEach((evt) => {
            video.addEventListener(evt, hideBuffering);
        });
    };

    // 4. Social Sidebar Fallback & Dynamic Loading
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    };

    const initAutoGrowTextareas = () => {
        document.querySelectorAll('.l-text-page__sidebar-form textarea').forEach((textarea) => {
            const baseRows = 2;
            const label = textarea.closest('label');
            const labelText = label ? label.textContent.trim() : '';

            if (labelText && !textarea.getAttribute('aria-label')) {
                textarea.setAttribute('aria-label', labelText);
            }

            const resize = () => {
                textarea.rows = baseRows;

                if (!textarea.value.trim() || textarea.scrollHeight <= textarea.clientHeight)
                    return;

                const styles = getComputedStyle(textarea);
                const lineHeight = parseFloat(styles.lineHeight) || 24;
                const padding =
                    (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
                const contentHeight = textarea.scrollHeight - padding;

                textarea.rows = Math.max(baseRows, Math.ceil(contentHeight / lineHeight));
            };

            textarea.rows = baseRows;
            textarea.addEventListener('input', resize);
            resize();
        });
    };

    // A fixed setTimeout alone races the widget script: platform.twitter.com/
    // connect.facebook.net/instagram's embed.js inject their iframe
    // asynchronously and can legitimately take longer than 5s under load or
    // rate-limiting, so a same-instant check can find "no iframe yet" -- but
    // that no longer matters for what the visitor SEES (see below), only for
    // when the real embed gets promoted in. Watching for the iframe to
    // actually appear (falling back to the timeout only when it never does)
    // avoids treating a merely-slow load as a permanent failure.
    //
    // "Appear" alone isn't enough, though -- confirmed live on X/Twitter's
    // own syndication widget: it can insert a real <iframe> into the DOM
    // (embed.querySelector('iframe') truthy) that then permanently stays
    // width:0/height:0/visibility:hidden, a genuine embed failure (X's
    // widget API has been unreliable since the platform changes), not a
    // still-loading state. isRenderedIframe() checks the iframe actually has
    // real layout size, and the observer keeps watching (attributes too,
    // since Twitter resizes the SAME iframe node via inline style rather
    // than replacing it) until either that becomes true or the timeout runs
    // out.
    const isRenderedIframe = (iframe) => {
        if (!iframe) return false;
        const rect = iframe.getBoundingClientRect();
        return (
            rect.width > 0 && rect.height > 0 && getComputedStyle(iframe).visibility !== 'hidden'
        );
    };

    // Fallback-first, not fallback-on-failure: the ACF-driven fallback card
    // (title/text/image/link -- genuinely static, no external data needed at
    // all) now renders visible by default (section-templates/twitter_
    // sidebar.php etc. no longer set display:none on it), and the embed
    // wrapper itself starts absolutely positioned + invisible via
    // social-embed-box's own CSS -- present and measurable for the widget
    // SDK to size/transform, but adding no extra layout height while the
    // fallback is what's actually showing. A visitor never stares at an
    // empty box waiting up to 5s for a widget that may not even load in
    // this environment (Twitter, confirmed unreliable here) -- they see real
    // content immediately, and it's replaced by the live embed only once one
    // is confirmed genuinely rendered. Same technique this session already
    // used for Facebook/Twitter's own OWN raw placeholder link/blockquote
    // (visibility:hidden, not display:none, so the SDK can still measure it).
    const watchEmbedForIframe = (container, embedSelector, fallbackSelector, timeoutMs = 5000) => {
        const embed = container.querySelector(embedSelector);
        const fallback = container.querySelector(fallbackSelector);

        const promoteEmbed = () => {
            if (fallback) fallback.style.display = 'none';
            if (embed) embed.classList.add('is-embed-loaded');
        };

        if (!embed) return;
        if (isRenderedIframe(embed.querySelector('iframe'))) {
            promoteEmbed();
            return;
        }

        const observer = new MutationObserver(() => {
            if (isRenderedIframe(embed.querySelector('iframe'))) {
                observer.disconnect();
                clearTimeout(timer);
                promoteEmbed();
            }
        });
        observer.observe(embed, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'width', 'height'],
        });

        // No action needed on timeout -- the fallback has been visible the
        // whole time; the embed simply never gets promoted, which is already
        // the correct end state (was previously an explicit showFallback()
        // call here; now a no-op).
        const timer = setTimeout(() => {
            observer.disconnect();
        }, timeoutMs);
    };

    const initSocialSidebar = () => {
        const facebook = document.querySelector('.js-facebook-sidebar');
        const instagram = document.querySelector('.js-instagram-sidebar');

        // X/Twitter's own syndication widget is left permanently disabled --
        // unlike a genuinely-slow-or-blocked load (what isRenderedIframe()
        // above guards against), it has a THIRD failure mode confirmed live
        // by the user: platform.twitter.com/widgets.js can insert a real,
        // correctly-sized, visible <iframe> (passes isRenderedIframe() outright)
        // whose own content is X's own empty-state UI ("Nothing to see here -
        // yet"/"View on X") -- a real X-branded card, off-theme, not an error
        // state at all from the browser's point of view. That iframe is
        // cross-origin (platform.twitter.com), so nothing on this page can
        // read its contents to tell "real tweets" from "X's own empty
        // placeholder" -- there is no reliable signal to detect this and fall
        // back correctly. Never loading the widget script at all guarantees
        // the ACF-driven fallback card (already the fallback-first default,
        // see watchEmbedForIframe's own comment above) is what visitors
        // always see for X, instead of an occasional off-brand card neither
        // this theme nor the user can control the contents of.

        // JCTCTAS-80: both SDKs are loaded ONLY once their sidebar actually
        // nears the viewport, not unconditionally at document.ready --
        // confirmed real (2026-09-04 performance audit): connect.facebook.
        // net's own fb-page widget throws 47-49 console errors on every
        // single page load that contains it (an internal Meta SDK behavior
        // attempting to also touch a connected Instagram account, 404ing
        // and then hitting Instagram's own X-Frame-Options: deny -- outside
        // this theme's control), even on pages with no instagram_sidebar
        // section at all. That noise/weight is unavoidable once the widget
        // genuinely loads, but there is no reason to pay it on every page
        // view regardless of whether the visitor ever scrolls far enough to
        // see the sidebar -- same VIEWPORT_MARGIN_PX=200 convention already
        // used by modules/hero-video-viewport.js and
        // modules/recommended-glow-viewport.js for the same "don't do the
        // expensive/noisy thing until it's actually about to be seen" idea.
        const loadWhenNearViewport = (el, src) => {
            if (!el) return;
            const observer = new IntersectionObserver(
                (entries, obs) => {
                    if (entries.some((entry) => entry.isIntersecting)) {
                        obs.disconnect();
                        loadScript(src).catch(() => {});
                    }
                },
                { rootMargin: '200px 0px' }
            );
            observer.observe(el);
        };

        loadWhenNearViewport(
            facebook,
            'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0'
        );
        if (facebook) {
            watchEmbedForIframe(facebook, '.c-facebook-sidebar__embed', '.js-facebook-fallback');
        }

        loadWhenNearViewport(instagram, 'https://www.instagram.com/embed.js');
        if (instagram) {
            watchEmbedForIframe(instagram, '.c-instagram-sidebar__embed', '.js-instagram-fallback');
        }
    };

    $(document).ready(() => {
        initMenu();
        initMenuAccordion();
        initStickyHeader();
        initHeroVideo();
        initHeroVideoStallRecovery();
        initAutoGrowTextareas();
        initSocialSidebar();
        initEvents();
        initEventCardTitleFit();
        initSwiperViewportAutoplay();
        initLqipReveal();
        initEventsAjax();
        initEventsPreviewScale();
        initBlogAjax();
        initPodcastsAjax();
        initEventFeedAjax();
        initCf7Turnstile();

        // Dynamic, not a static top-level import like every other module
        // here: a tracking/analytics-adjacent filename (this one bridges
        // CF7 submissions to Meta/TikTok pixels + GTM) is exactly what ad
        // blockers' filter lists target, and a BLOCKED static ES import
        // fails the whole module graph -- confirmed live, this took down
        // every other feature in this file (hero video, event/contact
        // modals, menu) for any visitor with such a blocker, not just the
        // tracking bridge itself. A blocked dynamic import only fails this
        // one optional feature.
        import('./modules/tracking-events.js')
            .then(({ default: initTrackingEvents }) => initTrackingEvents())
            .catch(() => {});
    });
})(window.jQuery);
